/*
 * Telemetry from a linked rig, shaped for drawing.
 *
 * The rule the chart must keep (pitriot-link-v1): a sample that is not
 * quality "good", is stale, or has no value is NOT drawn as if it were real.
 * A line that bridges a gap tells the reader the culture was measured when it
 * was not -- exactly the flat, plausible line a dead probe produces. So a
 * series is a list of segments, broken wherever a sample is untrustworthy,
 * and the untrustworthy samples are returned separately to be marked.
 */

export const CHART_CHANNELS = ['ph', 'dissolved_oxygen', 'temperature'];

const usable = (p) => p.quality === 'good' && !p.is_stale && typeof p.value === 'number' && Number.isFinite(p.value);

export function seriesFor(points, channel) {
  const own = (points || [])
    .filter(p => p.channel === channel)
    .map(p => ({ ...p, t: Date.parse(p.ts) }))
    .filter(p => Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t);

  const segments = [];
  const flagged = [];
  let current = [];
  for (const p of own) {
    if (usable(p)) {
      current.push(p);
    } else {
      if (current.length) segments.push(current);
      current = [];
      flagged.push(p);
    }
  }
  if (current.length) segments.push(current);

  const values = segments.flat().map(p => p.value);
  const unit = own.find(p => p.unit)?.unit || '';
  const latest = segments.length ? segments[segments.length - 1].slice(-1)[0] : null;
  return {
    segments,
    flagged,
    unit,
    latest,
    min: values.length ? Math.min(...values) : null,
    max: values.length ? Math.max(...values) : null,
  };
}

/* Linear scales for an SVG of the given size. A flat series gets a band so it
 * draws as a line through the middle rather than dividing by zero. */
export function scalesFor({ min, max }, startMs, endMs, width, height, pad = 8) {
  const span = Math.max(1, endMs - startMs);
  let lo = min;
  let hi = max;
  if (lo === null || hi === null) { lo = 0; hi = 1; }
  if (hi - lo < 1e-9) { lo -= 0.5; hi += 0.5; }
  const x = (t) => pad + ((t - startMs) / span) * (width - 2 * pad);
  const y = (v) => height - pad - ((v - lo) / (hi - lo)) * (height - 2 * pad);
  return { x, y, lo, hi };
}
